import { FC, useEffect, useState } from "react";
import { Box, Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { useRequestDownloadFileFn, useRequests } from "../../api";
import { PdfViewer } from "../../components/PdfViewer";
import { Document } from "@am-tax/wotc-common";

// Content types that browsers can display natively without a plugin
const PREVIEWABLE_TYPES = new Set([
    "application/pdf",
    "image/png",
    "image/jpeg",
    "image/gif",
    "image/webp",
    "image/svg+xml",
    "image/bmp",
    "image/avif",
    "text/plain",
    "text/html",
]);

function isPreviewable(contentType: string | undefined): boolean {
    if (!contentType) return false;
    const base = contentType.split(";")[0].trim().toLowerCase();
    return PREVIEWABLE_TYPES.has(base);
}

function isImage(contentType: string | undefined): boolean {
    return !!contentType && contentType.split(";")[0].trim().toLowerCase().startsWith("image/");
}

interface CandidateDetailsPdfViewerModalProps {
    isOpen: boolean;
    onClose: () => void;
    documentId: string | undefined;
}

export const CandidateDetailsPdfViewerModal: FC<CandidateDetailsPdfViewerModalProps> = ({ isOpen, onClose, documentId }) => {
    const downloadFileFn = useRequestDownloadFileFn();
    const { cId, eId, candidateId } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [fileData, setFileData] = useState<Blob>();
    const [fileUrl, setFileUrl] = useState<string>();
    const requestsQuery = useRequests(cId, eId, candidateId);
    const requests = requestsQuery.data || [];
    const request: any = requests.find((r: any) => r.documents?.some((d: any) => d.id === documentId)) || { documents: [] };
    const document: Document | undefined = request.documents.find((d: any) => d.id === documentId);
    const contentType = document?.contentType;
    const canPreview = isPreviewable(contentType);

    const progressCallback = (progress: number) => {
        setIsLoading(progress < 100);
    };

    useEffect(() => {
        if (isOpen && documentId && !fileData) {
            setTimeout(async () => {
                const result = await downloadFileFn(cId, eId, candidateId!, request.id, documentId!, progressCallback);
                setFileData(result);
                if (result) {
                    setFileUrl(URL.createObjectURL(result));
                }
            }, 0);
        }
        if (!isOpen) {
            setFileData(undefined);
            setFileUrl(undefined);
            setIsLoading(true);
        }
    }, [isOpen, documentId]);

    const renderPreview = () => {
        if (!canPreview || !fileUrl) return null;

        if (contentType === "application/pdf") {
            return <PdfViewer pdfData={fileData} height="calc(100vh - 280px)" minH="233pt" boxShadow={"5px 5px 10px 5px rgb(0 0 0 / 30%)"} />;
        }

        if (isImage(contentType)) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" maxH="calc(100vh - 280px)" overflow="auto">
                    <img
                        src={fileUrl}
                        alt={document?.name}
                        style={{ maxWidth: "100%", maxHeight: "calc(100vh - 300px)", objectFit: "contain", borderRadius: "4px" }}
                    />
                </Box>
            );
        }

        // text/plain, text/html, etc. — render in iframe
        return (
            <Box height="calc(100vh - 280px)" minH="233pt" boxShadow={"5px 5px 10px 5px rgb(0 0 0 / 30%)"}>
                <iframe src={fileUrl} width="100%" height="100%" title={document?.name} />
            </Box>
        );
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size={"4xl"}>
                <ModalOverlay />
                <ModalContent minH={canPreview ? "400pt" : undefined} maxH="100vh" mt="10px">
                    <ModalHeader>View File</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={"1.5rem"}>
                        <Box mb={4}>
                            <Text fontWeight="semibold">{document?.name}</Text>
                        </Box>
                        {canPreview ? (
                            <Box>{isLoading ? null : renderPreview()}</Box>
                        ) : (
                            <Text color="gray.500" fontSize="sm">
                                This file type cannot be previewed. Use the download button below.
                            </Text>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Box display="flex" justifyContent="flex-end" gap={4} mt={4}>
                            <Button
                                as="a"
                                href={fileUrl}
                                download={document?.name}
                                title={document?.name}
                                variant={"primary"}
                                isDisabled={isLoading}
                                isLoading={isLoading}
                            >
                                Download file
                            </Button>
                            <Button onClick={onClose}>Cancel</Button>
                        </Box>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};
